import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from './MarketingLayout';

const STUDIES = [
  {
    company: 'E-commerce Retailer', industry: 'Retail', flag: '🛍️', bg: '#eff6ff',
    headline: '$3,200 saved in the first month',
    challenge: 'A mid-sized online retailer was spending $15,000/month on Google Shopping and Search ads. Their conversion rate was dropping month over month despite increasing spend. Their account manager suspected fraudulent competitor clicks.',
    solution: 'After installing ClickSaver, they discovered 28% of their clicks were coming from just 47 repeat IP addresses — all flagged as automated traffic. ClickSaver blocked all 47 IPs automatically within the first week.',
    results: [
      { metric: '$3,200', label: 'Ad spend saved in month 1' },
      { metric: '28%', label: 'Of clicks identified as fraudulent' },
      { metric: '19%', label: 'Improvement in conversion rate' },
      { metric: '47', label: 'Fraudulent IPs blocked automatically' },
    ],
    quote: 'ClickSaver paid for itself in the first week. Our conversion rate is the highest it\'s been in 18 months.',
    author: 'Head of Digital Marketing',
  },
  {
    company: 'B2B SaaS Company', industry: 'Software', flag: '💻', bg: '#f0fdf4',
    headline: 'Competitor click attacks stopped cold',
    challenge: 'A SaaS startup was spending $8,000/month on branded and competitor keyword campaigns. They noticed their daily budget was being depleted by 11am every day, leaving the afternoon with zero ad coverage.',
    solution: 'ClickSaver identified a cluster of IPs from two competing companies repeatedly clicking their ads each morning. The IPs were automatically blocked and the click pattern stopped immediately. They also set up competitor keyword monitoring.',
    results: [
      { metric: '100%', label: 'Budget utilised throughout the day' },
      { metric: '2', label: 'Competitor IP ranges identified and blocked' },
      { metric: '34%', label: 'Reduction in cost per lead' },
      { metric: '3h', label: 'Setup time from install to protection' },
    ],
    quote: 'We always suspected competitors were draining our budget. ClickSaver confirmed it and stopped it automatically.',
    author: 'CEO & Co-founder',
  },
  {
    company: 'Local Services Business', industry: 'Home Services', flag: '🏠', bg: '#fff7ed',
    headline: '22% more leads at the same budget',
    challenge: 'A local plumbing and HVAC company was running Google Local Services Ads and Search campaigns with a $4,000/month budget. High CPCs and low lead volume were making the channel barely profitable.',
    solution: 'ClickSaver revealed that 18% of their clicks came from outside their service area — and a significant portion from mobile devices showing bot-like behaviour (too-fast page exits, no scroll activity). Geographic and behavioural filters were applied.',
    results: [
      { metric: '22%', label: 'More leads with the same budget' },
      { metric: '18%', label: 'Clicks from outside service area blocked' },
      { metric: '$720', label: 'Monthly ad spend saved' },
      { metric: '4.1×', label: 'ROI on ClickSaver subscription' },
    ],
    quote: 'I was about to give up on Google Ads. ClickSaver turned it into our best lead source.',
    author: 'Owner',
  },
];

export default function CaseStudies() {
  return (
    <MarketingLayout>
      {/* Hero */}
      <section style={{ padding: '80px 24px 60px', background: 'linear-gradient(180deg,#f8faff 0%,#fff 100%)', textAlign: 'center' }}>
        <div style={{ display: 'inline-block', background: '#f0fdf4', color: '#059669', fontSize: 12, fontWeight: 700, letterSpacing: '1.2px', textTransform: 'uppercase', padding: '5px 14px', borderRadius: 40, border: '1px solid #bbf7d0', marginBottom: 20 }}>Case Studies</div>
        <h1 style={{ fontSize: 48, fontWeight: 900, letterSpacing: '-2px', marginBottom: 16 }}>Real results from real customers</h1>
        <p style={{ fontSize: 18, color: '#475569', maxWidth: 480, margin: '0 auto' }}>See how businesses like yours stopped click fraud and reclaimed their ad spend.</p>
      </section>

      {/* Studies */}
      <section style={{ padding: '40px 24px 100px', background: '#fff' }}>
        <div style={{ maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 48 }}>
          {STUDIES.map((s, i) => (
            <div key={i} style={{ border: '1.5px solid #e2e8f0', borderRadius: 20, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,.04)' }}>
              {/* Header */}
              <div style={{ background: s.bg, padding: '28px 32px', display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ width: 56, height: 56, borderRadius: 14, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28 }}>{s.flag}</div>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#64748b', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: 4 }}>{s.industry}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: '#0f172a' }}>{s.headline}</div>
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: '32px 32px' }}>
                {/* Metrics */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1, background: '#e2e8f0', borderRadius: 12, overflow: 'hidden', marginBottom: 32 }}>
                  {s.results.map((r, j) => (
                    <div key={j} style={{ background: '#fff', padding: '20px 16px', textAlign: 'center' }}>
                      <div style={{ fontSize: 28, fontWeight: 900, color: '#2563eb', letterSpacing: '-1px', marginBottom: 4 }}>{r.metric}</div>
                      <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>{r.label}</div>
                    </div>
                  ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
                  <div>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>The Challenge</h3>
                    <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.7 }}>{s.challenge}</p>
                  </div>
                  <div>
                    <h3 style={{ fontSize: 14, fontWeight: 700, color: '#0f172a', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.5px' }}>The Solution</h3>
                    <p style={{ fontSize: 14, color: '#475569', lineHeight: 1.7 }}>{s.solution}</p>
                  </div>
                </div>

                {/* Quote */}
                <div style={{ background: '#f8fafc', borderLeft: '3px solid #2563eb', borderRadius: '0 10px 10px 0', padding: '16px 20px', marginTop: 28 }}>
                  <p style={{ fontSize: 15, color: '#334155', fontStyle: 'italic', lineHeight: 1.7, marginBottom: 8 }}>"{s.quote}"</p>
                  <p style={{ fontSize: 13, color: '#94a3b8', fontWeight: 600 }}>— {s.author}, {s.company}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: '60px 24px 80px', background: '#f8fafc', textAlign: 'center' }}>
        <h2 style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-1px', marginBottom: 14 }}>Start your own success story</h2>
        <p style={{ fontSize: 16, color: '#64748b', marginBottom: 28 }}>7-day free trial. See real results in the first week.</p>
        <Link to="/register" style={{ background: '#2563eb', color: '#fff', textDecoration: 'none', padding: '14px 36px', borderRadius: 12, fontSize: 16, fontWeight: 700 }}>Start Free Trial →</Link>
      </section>
    </MarketingLayout>
  );
}
